import type { FetchError, SalonStockerHttpTypes } from "@repo/api-sdk";
import sdk from "@repo/api-sdk";
import {
  queryOptions,
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient
} from "@tanstack/react-query";

import { useCallback, useEffect } from "react";

import { reportUnexpectedError } from "@/lib/errors";

import { queryKeys as productsQueryKeys } from "../products/queryKeys";
import { supplierMatchQueryKeys } from "./queryKeys";

/**
 * Client data layer for the buyer **Supplier Match** grid (ADR-0005):
 * `queryOptions` for the grid and a seller's Case B email state, the Order
 * assistant's once-per-page-load auto-check (a query, see
 * `useSupplierMatchAutoCheck`), and mutations for the grid's auto-check,
 * Retry, Release, Claim and request-email. Confirming the
 * emailed confirmation link itself is NOT here - the public confirm page calls
 * `sdk.store.supplierMatch.confirmEmail` directly, mirroring `EnterPage.tsx`
 * (a fresh, often-unauthenticated tab with no live cache for this salon to
 * interact with).
 *
 * `useCheckSupplierMatches` writes its response directly into the grid's
 * cache rather than invalidating - the response already *is* the refreshed
 * grid (ADR-0005 "Status is persisted"), so a follow-up refetch would be a
 * wasted round trip.
 */

// ============================================================
// Queries
// ============================================================

/** The full grid - one card per active seller, a pure local read. */
export function supplierMatchListQuery() {
  return queryOptions({
    queryKey: supplierMatchQueryKeys.list(),
    queryFn: () => sdk.store.supplierMatch.list(),
    staleTime: 5 * 60 * 1000
  });
}

export function useSupplierMatches(options?: { enabled?: boolean }) {
  return useQuery({ ...supplierMatchListQuery(), ...options });
}

let checkRunCount = 0;

/**
 * Run the check and write its response (the refreshed grid) into the list
 * cache, which is not refetched: the response already is it.
 *
 * The write happens only if the cache still belongs to whoever started the
 * check. The list key is not per customer, and a logout clears the cache while
 * a check may be in flight: a response landing after that would put the
 * previous salon's rows and verified emails back, fresh, for the next login.
 * So the run leaves a marker in the cache before the request and writes only
 * if the marker is still there (`clear()` removes it). With `timeoutMs`, a
 * request that takes longer is abandoned: it rejects, and a late response is
 * dropped, not written.
 */
async function runCheck(
  queryClient: QueryClient,
  options: { timeoutMs?: number } = {}
): Promise<SalonStockerHttpTypes.StoreSupplierMatchCheckResponse> {
  checkRunCount += 1;
  const marker = supplierMatchQueryKeys.checkRun(String(checkRunCount));
  queryClient.setQueryData(marker, true);
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const request = sdk.store.supplierMatch.checkAll();
    const { timeoutMs } = options;
    const data =
      timeoutMs === undefined
        ? await request
        : await Promise.race([
            request,
            new Promise<never>((_resolve, reject) => {
              timer = setTimeout(
                () =>
                  reject(
                    new Error(
                      `Supplier match check timed out after ${timeoutMs} ms`
                    )
                  ),
                timeoutMs
              );
            })
          ]);
    if (queryClient.getQueryData(marker) !== undefined) {
      queryClient.setQueryData<SalonStockerHttpTypes.StoreSupplierMatchListResponse>(
        supplierMatchQueryKeys.list(),
        { supplier_matches: data.supplier_matches }
      );
    }
    return data;
  } finally {
    clearTimeout(timer);
    queryClient.removeQueries({ queryKey: marker, exact: true });
  }
}

/**
 * How long the Order assistant waits for its auto-check. The SDK has no client
 * timeout, so a hung request would otherwise hold the card's placeholder until
 * the browser or a load balancer gave up.
 */
export const AUTO_CHECK_TIMEOUT_MS = 30_000;

/** How the Order assistant's auto-check ended. A failure is data, not an error. */
export type SupplierMatchAutoCheckResult = { ok: boolean };

/**
 * The Order assistant's auto-check: the same idempotent check the grid runs on
 * mount, but at most once per page load per customer. It is a query, not a
 * mutation, so its result lives in the in-memory cache and survives the drawer
 * unmounting on close: reopening the drawer or starting a new chat does not
 * run live supplier lookups again (a reload or a new tab does, the cache is
 * not persisted).
 *
 * A failed request, or one that takes longer than `AUTO_CHECK_TIMEOUT_MS`, is
 * returned as `{ ok: false }` rather than thrown. A query in the error state,
 * or one with no data, is stale and fetches again whenever `enabled` flips to
 * true, which it does on every list refetch; a settled result with an infinite
 * stale time never does. The caller shows nothing for the rest of the page
 * load instead of repeating live lookups. The failure is reported here, once,
 * where the request runs: a report from the component would repeat on every
 * mount that reads the cached failure.
 *
 * `enabled` is how the caller says a check is worth running at all.
 */
export function useSupplierMatchAutoCheck(
  customerId: string,
  options: { enabled: boolean }
) {
  const queryClient = useQueryClient();
  return useQuery<SupplierMatchAutoCheckResult>({
    queryKey: supplierMatchQueryKeys.autoCheck(customerId),
    // Deliberately does not read `signal`: that would make the query abort
    // when its last observer goes away, so closing the drawer mid-check (or
    // React remounting it) would throw the work away and run the lookups again
    // on the next open.
    queryFn: async () => {
      try {
        await runCheck(queryClient, { timeoutMs: AUTO_CHECK_TIMEOUT_MS });
        return { ok: true };
      } catch (error) {
        reportUnexpectedError(error, "order assistant supplier match check");
        return { ok: false };
      }
    },
    enabled: options.enabled,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });
}

/**
 * Read one seller's card fresh from the grid, or `null` when the read fails or
 * the seller is not on it. For a caller that needs the current `status` after
 * an error (e.g. why a claim was refused), which the error itself does not say.
 */
export function useReadSupplierMatch() {
  const queryClient = useQueryClient();
  return useCallback(
    async (sellerId: string) => {
      try {
        const data = await queryClient.fetchQuery({
          ...supplierMatchListQuery(),
          staleTime: 0
        });
        return (
          data.supplier_matches.find((match) => match.seller_id === sellerId) ??
          null
        );
      } catch {
        return null;
      }
    },
    [queryClient]
  );
}

/**
 * A seller's Case B email state. Typically gated on a detail dialog being
 * open (`enabled`) rather than fired for every card on grid mount.
 */
export function supplierMatchSellerStateQuery(sellerId: string) {
  return queryOptions({
    queryKey: supplierMatchQueryKeys.sellerState(sellerId),
    queryFn: () => sdk.store.supplierMatch.getSellerState(sellerId)
  });
}

export function useSupplierMatchSellerState(
  sellerId: string,
  options?: { enabled?: boolean }
) {
  return useQuery({ ...supplierMatchSellerStateQuery(sellerId), ...options });
}

/**
 * Refetch the grid each time `open` turns true (once per open, not on every
 * render), so a confirmation done in another tab or on another device shows
 * up in a detail dialog that is opened from a grid loaded long before. The
 * grid's cache is otherwise fresh for five minutes.
 */
export function useRefreshSupplierMatchesOnOpen(open: boolean) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (open) {
      void queryClient.invalidateQueries({
        queryKey: supplierMatchQueryKeys.lists()
      });
    }
  }, [open, queryClient]);
}

// ============================================================
// Mutations
// ============================================================

/**
 * Auto-check every not-yet-settled, lookup-capable seller. Idempotent - safe
 * to fire on every grid mount. The response is the refreshed grid, so it's
 * written straight into the list cache instead of triggering a refetch.
 */
export function useCheckSupplierMatches() {
  const queryClient = useQueryClient();

  return useMutation<
    SalonStockerHttpTypes.StoreSupplierMatchCheckResponse,
    FetchError,
    void
  >({
    mutationFn: () => runCheck(queryClient)
  });
}

/** Force a fresh lookup for one seller, regardless of its current status. */
export function useRetrySupplierMatch() {
  const queryClient = useQueryClient();

  return useMutation<
    SalonStockerHttpTypes.StoreRetrySupplierMatchResponse,
    FetchError,
    string
  >({
    mutationFn: (sellerId) => sdk.store.supplierMatch.retry(sellerId),
    onSuccess: (_data, sellerId) => {
      queryClient.invalidateQueries({
        queryKey: supplierMatchQueryKeys.lists()
      });
      queryClient.invalidateQueries({
        queryKey: supplierMatchQueryKeys.sellerState(sellerId)
      });
    }
  });
}

/** Release the salon's match for one seller - reverts checkout to the account email. */
export function useReleaseSupplierMatch() {
  const queryClient = useQueryClient();

  return useMutation<
    SalonStockerHttpTypes.StoreSupplierMatchEmailStateResponse,
    FetchError,
    string
  >({
    mutationFn: (sellerId) =>
      sdk.store.supplierMatch.releaseSellerMatch(sellerId),
    onSuccess: (_data, sellerId) => {
      // Releasing a claim changes the prices the salon is quoted (KAN-343).
      // Reset, not invalidate: invalidating keeps the cached (now wrong) prices
      // on screen until the refetch lands.
      queryClient.resetQueries({
        queryKey: productsQueryKeys.productsPricingAll()
      });
      queryClient.invalidateQueries({
        queryKey: supplierMatchQueryKeys.lists()
      });
      queryClient.invalidateQueries({
        queryKey: supplierMatchQueryKeys.sellerState(sellerId)
      });
    }
  });
}

export interface RequestSupplierMatchEmailInput {
  sellerId: string;
  email: string;
}

export interface ClaimSupplierMatchInput {
  sellerId: string;
  email: string;
}

/**
 * Claim a trade account with a claimable seller. Invalidates the seller's own
 * state and the grid - the card flips to `claimed` or `pending` at once.
 */
export function useClaimSupplierMatch() {
  const queryClient = useQueryClient();

  return useMutation<
    SalonStockerHttpTypes.StoreClaimSupplierMatchResponse,
    FetchError,
    ClaimSupplierMatchInput
  >({
    mutationFn: ({ sellerId, email }) =>
      sdk.store.supplierMatch.claim(sellerId, email),
    onSuccess: (_data, { sellerId }) => {
      // A claim changes the prices the salon is quoted (KAN-343). Reset, not
      // invalidate: invalidating keeps the cached retail prices on screen until
      // the refetch lands; resetting drops them so the loader shows.
      queryClient.resetQueries({
        queryKey: productsQueryKeys.productsPricingAll()
      });
      queryClient.invalidateQueries({
        queryKey: supplierMatchQueryKeys.sellerState(sellerId)
      });
      queryClient.invalidateQueries({
        queryKey: supplierMatchQueryKeys.lists()
      });
    }
  });
}

/**
 * Request an alternate lookup email for one seller (Case B). Invalidates
 * both the seller's own state AND the grid - a request can change the
 * grid's `pending_email` immediately, before any lookup runs.
 */
export function useRequestSupplierMatchEmail() {
  const queryClient = useQueryClient();

  return useMutation<
    SalonStockerHttpTypes.StoreSupplierMatchEmailStateResponse,
    FetchError,
    RequestSupplierMatchEmailInput
  >({
    mutationFn: ({ sellerId, email }) =>
      sdk.store.supplierMatch.requestSellerEmail(sellerId, email),
    onSuccess: (_data, { sellerId }) => {
      queryClient.invalidateQueries({
        queryKey: supplierMatchQueryKeys.sellerState(sellerId)
      });
      queryClient.invalidateQueries({
        queryKey: supplierMatchQueryKeys.lists()
      });
    }
  });
}
